import {
  combineReducers,
  configureStore,
  EnhancedStore,
  Middleware,
  Reducer,
} from '@reduxjs/toolkit';

/**
 * Creates an enhanced Redux store using an array of API objects and optional extra reducers.
 * This utility is adapted from RTK Query's helper function for easier testing and integration of multiple API slices.
 *
 * @param {A[]} apis - Array of API objects, each with middleware and reducers to incorporate into the store.
 * @param {R} [extraReducers] - Optional additional reducers to be combined into the store.
 * @returns {EnhancedStore} - The configured Redux store with combined reducers and middleware.
 */
export function createTestStore<
  A extends {
    reducer: Reducer<any, any>; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
    reducerPath: string;
    middleware: Middleware;
    util: {resetApiState(): any}; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
  },
  R extends Record<string, Reducer<any, any>> = Record<never, never>, // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
>(apis: A[], extraReducers?: R): EnhancedStore {
  /*
   * Modified version of RTK Query's helper function to accept an array of 'api' objects:
   * https://github.com/reduxjs/redux-toolkit/blob/master/packages/toolkit/src/query/tests/helpers.tsx
   */
  // eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
  const reducers = apis.reduce<Record<string, Reducer<any, any>>>(
    (acc, api) => {
      acc[api.reducerPath] = api.reducer;
      return acc;
    },
    {...extraReducers}
  );

  // eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
  const middlewareEnhancer = (gdm: any) => {
    let middleware = gdm({serializableCheck: false, immutableCheck: false});
    apis.forEach((api) => {
      middleware = middleware.concat(api.middleware);
    });
    return middleware;
  };

  return configureStore({
    reducer: combineReducers(reducers),
    middleware: middlewareEnhancer,
  });
}
